import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, isForbidden, schedule } from '@cwos/api-client';
import type { schedule as SCH } from '@cwos/api-client';
import { Pill, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { usePermissions } from '../permissions.js';
import { mayListWorkers } from '../permissions.js';
import { personDirectory } from '../personDirectory.js';
import { LoadFailure } from '../LoadFailure.js';
import { SettingsCard, SettingsHeader } from '../settings/common.js';
import { formatClock, formatDay } from '../time/timeLogic.js';
import {
  ASK_STATE_EXPLANATION,
  ASK_STATE_READING,
  askStateTone,
  groupByAskState,
  reasonReading,
  ATTRIBUTION_LABEL,
  OUTSTANDING_ASK_STATES,
  type AskState,
} from './cancellationLogic.js';

/**
 * UNANSWERED CANCELLATION ASKS — the queue the escalation had no reader for.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS CLOSES
 * ---------------------------------------------------------------------------
 * `escalateUnanswered` has written `escalated_to_user_id` since the domain
 * shipped, and there was no read that could find the row again: the only read on
 * the table took a cancellation id, and no surface had one. The parity ledger
 * put it in one sentence — "Escalated, unanswered asks are invisible to the
 * manager they escalate to." A manager was named as accountable for a question
 * they could not see.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A MANAGEMENT SCREEN AND HAS NO WORKER EQUIVALENT
 * ---------------------------------------------------------------------------
 * OPERATIONAL-LIBRARY-V1 §2.1: management sees intelligence, workers see
 * priorities; the founder's Ops directive says workers should not see an
 * operations control center. A list of everybody's outstanding call-out
 * questions is a view of the organization, so it is guarded by `schedule:manage`
 * — the org-wide roster key — and there is deliberately no worker-side version
 * of it. A worker's OWN outstanding question reaches them as a task on their own
 * queue, addressed to them, about one shift.
 *
 * ---------------------------------------------------------------------------
 * THE SWEEP IS A BUTTON, NEVER A RENDER
 * ---------------------------------------------------------------------------
 * `POST /schedule/cancellations/escalate-unanswered` WRITES: it stamps
 * `escalated_at` and names a manager. Calling it on load would mean opening this
 * screen escalated people's questions, which is a mutation disguised as a read.
 * So it is an explicit act, and its result is reported — including the rows that
 * escalated to NOBODY, which is a real finding about the org chart and not a
 * failure of the sweep.
 */

const MANAGE = 'schedule:manage';

/** How many rows to ask for. Not a page — see the note at the foot. */
const LIMIT = 200;

export function CancellationQueue() {
  const navigate = useNav();
  const permissions = usePermissions();
  const { has } = permissions;
  const canManage = has(MANAGE);
  const canListWorkers = mayListWorkers(permissions);
  const qc = useQueryClient();

  /**
   * OUTSTANDING first, everything second — and the two are different questions.
   *
   * The default asks only for `overdue` and `escalated`, which is the queue this
   * screen exists for. "Everything" adds `awaiting`, `answered` and `not_asked`,
   * and it is a separate request rather than a client-side filter precisely
   * because an empty OUTSTANDING list must be readable as a measured zero for
   * those two states — which it cannot be if the rows were fetched under a wider
   * filter and hidden here.
   */
  const [scope, setScope] = useState<'outstanding' | 'everything'>('outstanding');
  const [sweepResult, setSweepResult] = useState<{ total: number; unrouted: number } | null>(null);
  const [sweepError, setSweepError] = useState<string | null>(null);

  const states: readonly AskState[] | undefined =
    scope === 'outstanding' ? OUTSTANDING_ASK_STATES : undefined;

  const list = useQuery({
    queryKey: ['schedule', 'cancellations', scope],
    queryFn: () => schedule.cancellations({ states, limit: LIMIT }),
    enabled: canManage,
  });

  const workers = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canListWorkers,
  });

  const people = personDirectory(
    { data: workers.data, isLoading: workers.isLoading, isError: workers.isError },
    canListWorkers,
  );
  const workerName = (workerId: string): string => {
    const row = (workers.data ?? []).find((w) => w.id === workerId);
    if (row !== undefined) return row.displayName;
    if (workers.isLoading) return 'Loading…';
    if (workers.isError || !canListWorkers) return 'Name unavailable';
    return 'Someone you cannot view';
  };

  const sweep = useMutation({
    mutationFn: () => schedule.escalateUnansweredCancellations(),
    onSuccess: (escalated) => {
      setSweepError(null);
      setSweepResult({
        total: escalated.length,
        // A row that escalated to NOBODY is the org chart's answer, not the
        // sweep's failure, and it is the one a manager most needs to see: the
        // question has moved out of the worker's hands and into nobody's.
        unrouted: escalated.filter((e) => e.escalatedToUserId === null).length,
      });
      void qc.invalidateQueries({ queryKey: ['schedule', 'cancellations'] });
    },
    onError: (err: unknown) => {
      setSweepResult(null);
      setSweepError(
        err instanceof Error && err.message.trim() !== ''
          ? err.message
          : 'The sweep didn’t run. Nothing was escalated.',
      );
    },
  });

  // UNKNOWN IS NOT DENIED. `has()` answers false while `/auth/me` is in flight
  // and false after it failed, so a bare `!canManage` would tell somebody who
  // holds `schedule:manage` perfectly well that this queue is not theirs — on
  // every cold load. `authorityGate` keeps the four apart and only `refused` is
  // entitled to the sentence.
  const manageGate = authorityGate(permissions, MANAGE);
  if (!gateOpen(manageGate)) {
    return (
      <GateNotice
        gate={manageGate}
        what="see the whole roster’s unanswered cancellation questions"
        heading="This queue is not shown to you"
        detail="A list of everybody’s outstanding call-out questions is a view of the organization and needs permission to manage the schedule. That is a refusal about your account, not an empty queue — do not read it as “nobody owes an answer”."
        onRetry={permissions.refetch}
      />
    );
  }

  const groups = groupByAskState(list.data ?? []);

  return (
    <div className="rn-col" style={{ maxWidth: 860 }}>
      <SettingsHeader
        title="Unanswered cancellation asks"
        blurb="Phibian asks a staff member once why a shift was called off. What follows an unanswered ask is their manager, not another message — and this is where that lands."
      />

      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">
            {scope === 'outstanding' ? 'Somebody owes an answer' : 'Every cancellation'}
          </h2>
          <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
            <label className="rn-muted" htmlFor="cancellation-scope">
              Show
            </label>
            <select
              id="cancellation-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value as 'outstanding' | 'everything')}
            >
              <option value="outstanding">Only what is owed</option>
              <option value="everything">Every cancellation</option>
            </select>
            <button
              type="button"
              className="rn-btn-quiet"
              disabled={sweep.isPending}
              onClick={() => sweep.mutate()}
            >
              {sweep.isPending ? 'Escalating…' : 'Escalate what is past its deadline'}
            </button>
          </div>
        </div>

        {sweepError !== null ? (
          <p role="status" className="rn-muted" style={{ color: 'var(--color-urgent-deep)' }}>
            {sweepError}
          </p>
        ) : sweepResult !== null ? (
          <p role="status" className="rn-muted">
            {sweepResult.total === 0
              ? 'Nothing was past its deadline. That is a measured zero, not a sweep that failed.'
              : `${sweepResult.total} ${sweepResult.total === 1 ? 'question' : 'questions'} escalated.`}
            {sweepResult.unrouted > 0
              ? ` ${sweepResult.unrouted} of them reached NOBODY: the org chart names no authority-bearing manager for those workers today, so the question has left the worker and landed with no one.`
              : ''}
          </p>
        ) : null}

        {list.isLoading ? (
          <SkeletonSet surface="table" rows={4} />
        ) : list.isError ? (
          isForbidden(list.error) ? (
            <p className="rn-muted" style={{ margin: 0 }}>
              This queue is not shown to you. That is an answer about your account — do not read it
              as “nobody owes an answer”.
            </p>
          ) : (
            <LoadFailure
              error={list.error}
              message="We couldn’t read the cancellation queue just now."
              onRetry={() => void list.refetch()}
            />
          )
        ) : groups.length === 0 ? (
          <SuccessEmpty
            heading={
              scope === 'outstanding' ? 'Nobody owes an answer' : 'No cancellations in the record'
            }
            message={
              scope === 'outstanding'
                ? 'No question is past its deadline and none has been escalated. This is a measured zero over those two states, and it says nothing about questions still inside their window — switch to “Every cancellation” for those.'
                : 'No shift has been called off through the cancellation door. A shift cancelled through the plain door leaves no record here.'
            }
          />
        ) : (
          groups.map((group) => (
            <section key={group.state} style={{ marginTop: 'var(--space-12)' }}>
              <div className="rn-row" style={{ gap: 'var(--space-8)', alignItems: 'center' }}>
                <Pill variant={askStateTone(group.state)} dot>
                  {ASK_STATE_READING[group.state]}
                </Pill>
                <span className="rn-muted">
                  {group.entries.length} {group.entries.length === 1 ? 'shift' : 'shifts'}
                </span>
              </div>
              <p className="rn-muted" style={{ margin: 'var(--space-4) 0 var(--space-8)' }}>
                {ASK_STATE_EXPLANATION[group.state]}
              </p>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {group.entries.map((entry) => (
                  <QueueRow
                    key={entry.cancellation.id}
                    entry={entry}
                    workerName={workerName(entry.cancellation.workerId)}
                    escalatedToName={
                      entry.cancellation.escalatedToUserId === null
                        ? null
                        : people.name(entry.cancellation.escalatedToUserId)
                    }
                    onOpenWorker={() => navigate(`/people/${entry.cancellation.workerId}`)}
                  />
                ))}
              </ul>
            </section>
          ))
        )}

        {/*
          WHAT DID NOT FIT IS NOT HIDDEN. The route caps at 500 and this asks for
          200; a full page means there may be more, and saying so is the
          difference between a list and a claim about the world.
        */}
        {(list.data ?? []).length === LIMIT ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
            This is the most recent {LIMIT}. There may be older ones that did not fit — do not read
            the bottom of this list as the beginning of the record.
          </p>
        ) : null}
      </SettingsCard>
    </div>
  );
}

function QueueRow({
  entry,
  workerName,
  escalatedToName,
  onOpenWorker,
}: {
  entry: SCH.CancellationListEntry;
  workerName: string;
  escalatedToName: string | null;
  onOpenWorker: () => void;
}) {
  const view = entry.cancellation;
  const reason = reasonReading(view.reasonCategory);
  return (
    <li className="tc-row" style={{ padding: 'var(--space-8) 0' }}>
      <div className="rn-row" style={{ justifyContent: 'space-between', gap: 'var(--space-8)' }}>
        <button type="button" className="rn-link" style={{ padding: 0 }} onClick={onOpenWorker}>
          {workerName}
        </button>
        <span className="rn-muted">
          called off {formatDay(view.cancelledAt)} · {formatClock(view.cancelledAt)}
          {view.sameDay ? ' · same day' : ''}
        </span>
      </div>
      <p className="rn-muted" style={{ margin: 0 }}>
        Attributable to {ATTRIBUTION_LABEL[view.attributedTo].toLowerCase()}.
        {view.askDeadlineAt !== null && view.reasonGivenAt === null
          ? ` Answer was due ${formatDay(view.askDeadlineAt)}.`
          : ''}
        {reason !== null ? ` Reason given: ${reason}.` : ''}
      </p>
      {view.escalatedAt !== null ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          {escalatedToName === null ? (
            <strong>
              Escalated to nobody — the org chart names no authority-bearing manager for this
              worker. The question is unanswered and unowned.
            </strong>
          ) : (
            <>Escalated to {escalatedToName}.</>
          )}
        </p>
      ) : null}
      {/*
        THE OBLIGATION IS REPORTED SEPARATELY FROM THE ASK, because they are
        different facts and only one of them is about a person. A visit nobody
        put back on the calendar is the operational signal in this whole family;
        an unanswered question is not.
      */}
      {view.rescheduleRequired && view.rescheduledShiftId === null ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          The client still needs this visit.
          {view.rescheduleTaskId === null
            ? ' No task is tracking the replacement.'
            : ' A task is tracking the replacement.'}
        </p>
      ) : null}
    </li>
  );
}
